import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Button, Box,  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
    Grid,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert } from '@mui/material';
import { 
  Email, LocationOn, Person, CalendarToday,
  MusicNote, Article, ReceiptLong, ConfirmationNumberRounded,
  ModeEdit,
  Update,
  Adjust,
  LocalActivity,
  Groups2,
  ArrowForward,
  AttachMoney,
  People,
  Euro,
  FactCheck,
  AttachFile,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Performance, BandRecommendation, Venue } from '../../types/mongodb';
import axiosInstance from '../../utils/axiosConfig';
import ContactModal from './ContactModal';
import { Strand } from '../../types/Strand';
import TitoSlug from './TitoSlug'; 
import CreatePOModal from '../PO/CreatePOModal';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { RequestQuote } from '@mui/icons-material';
import InvoiceRequestModal, { InvoiceRequestData } from './InvoiceRequestModal';
import { format } from 'date-fns';


interface ActionSectionProps {
  onManageStaffClick: () => void;  // Add this line
  performance: Performance & {
    band?: string | BandRecommendation;
    venue?: string | Venue;
  };
}

interface BandInfo {
  name: string; 
  primaryContact: string;
  email: string;
  phone: string;
}

// Add these interfaces within the existing interfaces:
interface ContactInfo {
  name: string;
  email?: string;
  phone?: string;
  code?: string;
  type: 'artist' | 'venue' | 'technical';
}

interface ContractInfo {
  _id: string;
  contractNumber: string;
  artistName: string;
  status: 'draft' | 'pending_signature' | 'signed' | 'cancelled';
  documentUrl?: string;
  eventDate: string;
  venue: string;
  createdAt: string;
}

interface SignedContractFile {
  name: string;
  url: string;
  uploadedAt: string;
  type: string;
}

const ActionSection: React.FC<ActionSectionProps> = ({ performance, onManageStaffClick}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [currentContact, setCurrentContact] = useState<ContactInfo | null>(null);
  const [artistContact, setArtistContact] = useState<ContactInfo | null>(null);
  const [venueContact, setVenueContact] = useState<ContactInfo | null>(null);
  const [titoSalesDialogOpen, setTitoSalesDialogOpen] = useState(false);
  const [titoSalesLoading, setTitoSalesLoading] = useState(false);
  const [titoSalesError, setTitoSalesError] = useState<string | null>(null);
  const [titoSalesData, setTitoSalesData] = useState<any>(null);
  const [titoSlug, setTitoSlug] = useState<string>('');
  const [ticketsNotOnSale, setTicketsNotOnSale] = useState(false);
  const [poModalOpen, setPoModalOpen] = useState(false);
  const [existingPOs, setExistingPOs] = useState<any[]>([]);
  const [loadingPOs, setLoadingPOs] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [technicalContact, setTechnicalContact] = useState<ContactInfo | null>(null);
  const [existingSchedule, setExistingSchedule] = useState<any>(null);
  const [checkingSchedule, setCheckingSchedule] = useState(false);
  const [existingContracts, setExistingContracts] = useState<ContractInfo[]>([]);
  const [signedContractFiles, setSignedContractFiles] = useState<SignedContractFile[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);



    const hasBandRecord = performance.band && 
    typeof performance.band === 'object' && 
    '_id' in performance.band;

  const hasVenueRecord = performance.venue &&
    typeof performance.venue === 'object' &&
    '_id' in performance.venue;

const fetchArtistContact = async () => {
  try {
    if (hasBandRecord) {
      const band = performance.band as BandRecommendation;
      setArtistContact({
        name: band.artistName,
        email: band.contactEmail,
        phone: band.contactPhone,
        type: 'artist'
      });
    } else if (performance.simpleBandName) {
      const response = await axiosInstance.get('/api/programmings/search', {
        params: { artistName: performance.simpleBandName }
      });

      if (response.data && response.data.length > 0) {
        const latestProgramming = response.data[0];
        setArtistContact({
          name: latestProgramming.artistName,
          email: latestProgramming.email,
          phone: latestProgramming.phone,
          type: 'artist'
        });
        return;
      }
    }

    // Fallback: use contact info directly from performance
    if (performance.contactEmail || performance.contactPhone || performance.primaryContact) {
      setArtistContact({
        name: performance.primaryContact || performance.simpleBandName || '',
        email: performance.contactEmail || '',
        phone: performance.contactPhone || '',
        type: 'artist'
      });
    }
  } catch (error) {
    console.error('Error fetching artist contact:', error);
  }
};
  
  const fetchVenueContact = async () => {
    try {
      let venue: Venue | null = null;

      if (hasVenueRecord) {
        venue = performance.venue as Venue;
      } else if (typeof performance.venue === 'string') {
        const response = await axiosInstance.get(`/api/venues/${performance.venue}`);
        venue = response.data;
      }

      if (venue) {
        setVenueContact({
          name: venue.contactInfo?.name || venue.name,
          email: venue.contactInfo?.email || '',
          phone: venue.contactInfo?.phone || '',
          type: 'venue'
        });

        if (venue.contactInfo?.technical) {
          setTechnicalContact({
            name: venue.contactInfo.technical.name,
            email: venue.contactInfo.technical.email,
            phone: venue.contactInfo.technical.phone || '',
            type: 'technical'
          });
        }
      }
    } catch (error) {
      console.error('Error fetching venue contact:', error);
    }
  };

// Function to fetch Tito sales data using the existing slug
const fetchTitoSalesData = async (slug: string) => {
  try {
    setTitoSalesLoading(true);
    setTitoSalesError(null);
    setTicketsNotOnSale(false);
    
    // Fetch the event data from the Tito API through your backend
    const eventResponse = await axiosInstance.get(`/api/tito/events/${slug}`);
    
    if (!eventResponse.data || !eventResponse.data.event) {
      setTicketsNotOnSale(true);
      throw new Error('Event not found in Tito');
    }
    
    // Fetch additional data about the event
    const [registrationsResponse, releasesResponse] = await Promise.all([
      axiosInstance.get(`/api/tito/${slug}/registrations`),
      axiosInstance.get(`/api/tito/${slug}/releases`)
    ]);
    
    // Process the data
    const eventData = eventResponse.data.event;
    const registrations = registrationsResponse.data?.registrations || [];
    const releases = releasesResponse.data?.releases || [];
    
    // Format the data for display
    const formattedData = {
      event: eventData,
      registrations,
      releases,
      ticketTypes: releases.map((release: any) => ({
        name: release.title,
        quantity: release.quantity,
        sold: release.tickets_count,
        price: release.price,
        revenue: release.tickets_count * release.price
      })),
      totalSold: releases.reduce((sum: number, release: any) => 
        sum + (parseInt(release.tickets_count) || 0), 0),
      totalRevenue: releases.reduce((sum: number, release: any) => 
        sum + (parseInt(release.tickets_count) || 0) * (parseFloat(release.price) || 0), 0)
    };
    
    setTitoSalesData(formattedData);
  } catch (error: any) {
    console.error('Error fetching Tito sales data:', error);
    setTicketsNotOnSale(true);
    if (error.response?.status === 404) {
      setTitoSalesError('Event not set up in ticketing system yet');
    } else {
      setTitoSalesError(error.message || 'Failed to fetch ticket sales data');
    }
  } finally {
    setTitoSalesLoading(false);
  }
};

const handleRequestInvoice = () => {
  setInvoiceModalOpen(true);
};

const sendInvoiceRequest = async (data: InvoiceRequestData) => {
  try {
    const response = await axiosInstance.post('/api/email/invoice-request', data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to send invoice request');
    }
    
  } catch (error) {
    console.error('Error sending invoice request:', error);
    throw error;
  }
};

const handleCopyTitoSlug = () => {
  navigator.clipboard.writeText(titoSlug);
  // If you're using react-toastify:
  // toast.success('Tito slug copied to clipboard!');
  // If not, use a regular alert:
  alert('Tito slug copied to clipboard!');
};

// Use a ref to access the slug generation logic from your TitoSlug component
const titoSlugRef = React.useRef<HTMLDivElement>(null);

  
  // Add useEffect hook:
  useEffect(() => {
    fetchArtistContact();
    fetchVenueContact();
  }, [performance]);
  
  useEffect(() => {
  if (performance?._id) {
    fetchContractData();
  }
}, [performance]);

const fetchContractData = async () => {
  setContractsLoading(true);
  try {
    // Fetch generated contracts from database (same logic as TechnicalPhase)
    const performanceId = performance._id;
    const artistName = performance.simpleBandName || 
      (typeof performance.band === 'object' ? performance.band.artistName : '');

    console.log('Looking for contracts matching:', {
      performanceId: performanceId,
      artistName: artistName
    });

    // Get all contracts from database
    const contractsResponse = await axiosInstance.get('/api/contracts');
    
    // Filter contracts that match this performance/artist
    const matchingContracts = contractsResponse.data.filter((contract: any) => {
      const contractPerformanceId = contract.contractData?.performanceId;
      const contractArtistName = contract.artistName?.toLowerCase() || '';
      const searchArtistName = artistName.toLowerCase();

      const matchesPerformance = contractPerformanceId === performanceId;
      const matchesArtist = contractArtistName.includes(searchArtistName) || 
                           searchArtistName.includes(contractArtistName);

      return matchesPerformance || matchesArtist;
    });

    console.log('Found matching contracts:', matchingContracts);
    setExistingContracts(matchingContracts);

    // Fetch signed contract files from resources
    await fetchSignedContractFiles();

  } catch (error) {
    console.error('Error fetching contract data:', error);
    setExistingContracts([]);
    setSignedContractFiles([]);
  } finally {
    setContractsLoading(false);
  }
};

const fetchSignedContractFiles = async () => {
  try {
    // First get the resource by performance ID
    const resourceResponse = await axiosInstance.get(`/api/resources/performance/${performance._id}`);
    const resourceId = resourceResponse.data?._id;

    if (!resourceId) {
      console.log('No resource found for this performance');
      setSignedContractFiles([]);
      return;
    }

    // Then get files using the resource ID
    const filesResponse = await axiosInstance.get(`/api/resources/${resourceId}/files`);
    const data = filesResponse.data || {};

    // Get signed contract files from technical files
    const technicalFiles = data.technical ? Object.values(data.technical).flat() : [];
    const signedContracts = (technicalFiles as SignedContractFile[]).filter((file) =>
      file.type === 'signed_contract' || file.name.toLowerCase().includes('signed')
    );

    setSignedContractFiles(signedContracts);

  } catch (error: any) {
    if (error.response?.status === 404) {
      console.log('No resource files found yet');
      setSignedContractFiles([]);
    } else {
      console.error('Error fetching signed contract files:', error);
      setSignedContractFiles([]);
    }
  }
};

const getContractButtonProps = () => {
  const hasGeneratedContract = existingContracts.length > 0;
  const hasSignedContract = signedContractFiles.length > 0;
  
  if (contractsLoading) {
    return {
      text: 'Loading...',
      color: 'info' as const,
      action: () => {},
      disabled: true
    };
  }

  // If there's a signed contract uploaded, show "View Contract"
  if (hasSignedContract) {
    return {
      text: 'View Contract',
      color: 'success' as const,
      action: () => {
        // Navigate to resources technical tab to view the signed contract
        navigate(`/admin/resources/${performance._id}?tab=technical`);
      },
      disabled: false
    };
  }

  // If there's a generated contract but no signed one, show "Edit Contract"
  if (hasGeneratedContract) {
    const latestContract = existingContracts[0]; // Assuming they're sorted by creation date
    return {
      text: 'Edit Contract',
      color: 'warning' as const,
      action: () => {
        // Navigate to edit the existing contract
        navigate(`/admin/contracts/edit/${latestContract._id}`);
      },
      disabled: false
    };
  }

  // No contracts exist, show "Create Contract"
  return {
    text: 'Create Contract',
    color: 'warning' as const,
    action: handleCreateContract, // Your existing function
    disabled: false
  };
};



const getBandInfo = async (): Promise<BandInfo> => {
  if (hasBandRecord) {
    const band = performance.band as BandRecommendation;
    return {
      name: band.artistName,
      primaryContact: band.contactName || '',
      email: band.contactEmail || '',
      phone: band.contactPhone || ''
    };
  }

  if (performance.simpleBandName) {
    try {
      const response = await axiosInstance.get('/api/programmings/search', {
        params: { artistName: performance.simpleBandName }
      });

      if (response.data && response.data.length > 0) {
        const latest = response.data[0];
        return {
          name: latest.artistName,
          primaryContact: latest.primaryContact || '',
          email: latest.email || '',
          phone: latest.phone || ''
        };
      }
    } catch (err) {
      console.error('Programming lookup failed', err);
    }
  }

  // Fallback: use performance info directly
  return {
    name: performance.simpleBandName || '',
    primaryContact: performance.primaryContact || '',
    email: performance.contactEmail || '',
    phone: performance.contactPhone || ''
  };
};

// Update your handleViewTicketSales function to use the slug from your component
const handleViewTicketSales = () => {
  // Get the slug from the hidden TitoSlug component
  // For now we'll just calculate it directly
  const artistName = performance.simpleBandName || 
    (typeof performance.band === 'object' ? performance.band?.artistName : '');
  const venueName = typeof performance.venue === 'object' ? 
    performance.venue?.name?.toLowerCase() : 'cooler';
  const date = new Date(performance.startTime);
  const month = date.toLocaleString('en-US', { month: 'short' }).toLowerCase();
  const year = date.getFullYear().toString().slice(-2);
  
  const cleanText = (text: string) => {
    return text
      .toLowerCase()
      .replace(/^the\s+/i, '')
      .replace(/\s+the\s+/g, ' ')
      .replace(/['']/g, '')
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };
  
  const cleanArtistName = cleanText(artistName || '');
  const cleanVenueName = cleanText(venueName || '');
  const slug = `${cleanArtistName}-${cleanVenueName}-${month}${year}`;
  
  console.log('WORKING CODE - Artist name:', artistName);
  console.log('WORKING CODE - Venue name:', venueName);
  console.log('WORKING CODE - Date:', date);
  console.log('WORKING CODE - Generated slug:', slug);
  
  setTitoSlug(slug);
  setTitoSalesData(null);
  setTitoSalesDialogOpen(true);
};

// Add this effect to fetch the data when the dialog opens and we have a slug
useEffect(() => {
  if (titoSalesDialogOpen && titoSlug) {
    fetchTitoSalesData(titoSlug);
  }
}, [titoSalesDialogOpen, titoSlug]);

useEffect(() => {
  const checkExistingPOs = async () => {
    if (!performance?._id) return;
    
    try {
      setLoadingPOs(true);
      const pos = await purchaseOrderService.getPurchaseOrdersByPerformance(performance._id);
      setExistingPOs(pos);
    } catch (error) {
      console.error('Error checking purchase orders:', error);
    } finally {
      setLoadingPOs(false);
    }
  };
  
  checkExistingPOs();
}, [performance?._id]);


// Add this useEffect to check for existing schedule
useEffect(() => {
 const checkExistingSchedule = async () => {
   if (!performance?._id) return;
   
   try {
     setCheckingSchedule(true);
     const response = await axiosInstance.get(`/api/schedules/by-performance/${performance._id}`);
     
     // Handle the backend's response format with exists flag
     if (response.data.exists) {
       setExistingSchedule(response.data);
     } else {
       setExistingSchedule(null);
     }
   } catch (error: any) {
     if (error.response?.status !== 404) {
       console.error('Error checking existing schedule:', error);
     }
     // Any error means no schedule exists
     setExistingSchedule(null);
   } finally {
     setCheckingSchedule(false);
   }
 };
 
 checkExistingSchedule();
}, [performance?._id]);

  
  const handleTicketReview = async () => {
    try {
      setIsLoading(true);
      // Search for ticket configuration using the performance ID
      const response = await axiosInstance.get('/api/ticket-configs/search', {
        params: {
          performanceId: performance._id,
          selectionMethod: 'event'
        }
      });
      
      if (response.data && response.data._id) {
        // If configuration exists, navigate to its page
        navigate(`/tix-list/${response.data._id}`);
      } else {
        // If no configuration exists, navigate to the main ticketing page
        navigate('/tix-list');
      }
    } catch (error) {
      console.error('Error checking ticket configuration:', error);
      // On error, default to main ticketing page
      navigate('/tix-list');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateContract = async () => {
    try {
        setIsLoading(true);
      
        // Safely access nested properties
        const costs = performance.costs || {};
        const travel = costs.travel || {};
        const accommodation = costs.accommodation || {};
        const perDiem = costs.perDiem || {};

        // Get band info from available sources
        const bandInfo = await getBandInfo();

        // Extract the strand ID correctly
        let strandName = '';
        const strandId = (performance.strandId as Strand)?._id || performance.strandId;  // Extract the _id if it's an object

        console.log("🔍 Checking extracted strandId:", strandId);
        
        if (strandId && typeof strandId === 'string') { 
            try {
                console.log("🚀 Fetching strand data...");
                const strandResponse = await axiosInstance.get(`/api/strands/${strandId}`);
                strandName = strandResponse.data?.name || '';
                console.log("✅ Strand name:", strandName);
            } catch (error) {
                console.error("❌ Error fetching strand data:", error);
            }
        } else {
            console.error("⛔ Invalid strandId:", strandId);
        }

        // Prepare contract data
        const contractData = {
            Item: bandInfo.name,
            Contact: bandInfo.primaryContact,
            Email: bandInfo.email,
            Phone: bandInfo.phone,
            'Event Date': performance.startTime || null,
            Venue: hasVenueRecord ? (performance.venue as Venue).name : '',
            Address: hasVenueRecord ? (performance.venue as Venue).location : '',
'Soundcheck Time': (() => {
  // If there's no performance start time, return null
  if (!performance.startTime) return null;
  
  // Create a new date object based on the performance date
  const soundcheckTime = new Date(performance.startTime);
  
  // Set the time to 16:30 (4:30pm)
  soundcheckTime.setHours(4, 30, 0, 0);
  
  return soundcheckTime;
})() || null,
            'Performance Type': 'Performance - Full Concert',
            'Min Duration': '90 minutes',
            Fees: costs.bandFee?.toString() || '0',
            'Int\'l Flights': travel.flights > 0 ? 'Required' : 'N/A',
            'Ground Transport': travel.groundTransport > 0 ? 'Required' : 'N/A',
            'Accommodation Type': accommodation.singleRooms > 0 
                ? 'Single Room' 
                : accommodation.twinRooms > 0 
                    ? 'Twin Room' 
                    : 'Not Required',
            'Accommodation Qty': ((accommodation.singleRooms || 0) + (accommodation.twinRooms || 0)).toString(),
            'PD Amount': perDiem.dailyRate?.toString() || '0',
            'PD Days': perDiem.numberOfDays?.toString() || '1',
            'PD Quantity': hasBandRecord 
                ? ((performance.band as BandRecommendation).totalMembers || 1).toString() 
                : '1',
            performanceId: performance._id,
            'Stage Time': performance.startTime || null,
            Doors: performance.startTime || null,
            'Soundcheck Duration': '60 minutes',
            'Payment Type': 'bank-transfer',
            'Activities Master': strandName, // ✅ Now correctly set
            'IMC Code': ''
        };

        console.log("✅ Contract Data Prepared:", contractData);

        console.log("✅ Contract Data Prepared:", contractData);
        console.log("🚀 Navigating to Contract Page with Data...");

        setTimeout(() => {
          navigate('/admin/contracts/new', { state: contractData });
        }, 500);

} catch (error) {
        console.error("Error creating contract:", error);
    } finally {
        setIsLoading(false);
    }
};

  const toNum = (val: any): number => {
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  };

const handleOpenPOModal = () => {
  setPoModalOpen(true);
};


// Add handler functions
const handlePOButtonClick = () => {
  if (existingPOs.length > 0) {
    // Navigate to PO list filtered by this performance
    navigate(`/admin/purchase-orders?performanceId=${performance._id}`);
  } else {
    // Open create modal
    setPoModalOpen(true);
  }
};

const handlePOSuccess = () => {
  // Refresh the PO list
  const checkExistingPOs = async () => {
    try {
      const pos = await purchaseOrderService.getPurchaseOrdersByPerformance(performance._id);
      setExistingPOs(pos);
    } catch (error) {
      console.error('Error refreshing purchase orders:', error);
    }
  };
  
  checkExistingPOs();
  alert('Purchase Order created successfully!');
};

const handleCreateSchedule = async () => {
 console.log('🔍 handleCreateSchedule called');
 console.log('🔍 existingSchedule:', existingSchedule);
 console.log('🔍 performance._id:', performance._id);
 
 // If schedule already exists, navigate to it
 if (existingSchedule && existingSchedule.exists) {
   console.log('🔍 Navigating with scheduleId:', existingSchedule._id);
console.log('🔍 existingSchedule object:', existingSchedule);
   // Prepare template variables for navigation
   const bandInfo = await getBandInfo();
   const venueData = hasVenueRecord ? performance.venue as Venue : null;
   const variables = {
     ARTIST: bandInfo.name,
     VENUE: venueData?.name || '',
     VENUE_ADDRESS: venueData?.location || '',
     DATE: performance.startTime ? format(new Date(performance.startTime), 'dd-MM-yyyy') : '',
     CONTACT_NAME: bandInfo.primaryContact || '',
     CONTACT_EMAIL: bandInfo.email || '',
     CONTACT_PHONE: bandInfo.phone || ''
   };

   navigate('/admin/performances/schedule', { 
     state: { 
      scheduleId: existingSchedule.schedule._id,
      resourceId: existingSchedule.schedule.resourceId, 
       performanceData: {
         bandName: variables.ARTIST,
         venue: {
           name: variables.VENUE,
           location: variables.VENUE_ADDRESS
         },
         timing: {
           date: performance.startTime ? new Date(performance.startTime) : null
         },
         contactName: variables.CONTACT_NAME,
         contactEmail: variables.CONTACT_EMAIL,
         contactPhone: variables.CONTACT_PHONE,
         programmingId: performance.programmingId
       }
     }
   });
   return;
 }

 console.log('🔍 Creating new schedule');
 // Create new schedule
 let venueData: Venue | null = null;
 let bandInfo: any = null;
 
 try {
   setIsLoading(true);
   console.log('🔍 setIsLoading(true) called');
   
   // Get venue info
   venueData = hasVenueRecord ? performance.venue as Venue : null;
   console.log('🔍 venueData:', venueData);
   if (!venueData) {
     throw new Error('Venue information is required to create a schedule');
   }
   
   // Get band info
   bandInfo = await getBandInfo();
   console.log('🔍 bandInfo:', bandInfo);
   
   console.log('Creating schedule with venue data:', venueData);
   console.log('Band info:', bandInfo);

   // Prepare template variables
   const variables = {
     ARTIST: bandInfo.name,
     VENUE: venueData.name,
     VENUE_ADDRESS: venueData.location,
     DATE: performance.startTime ? format(new Date(performance.startTime), 'dd-MM-yyyy') : '',
     CONTACT_NAME: bandInfo.primaryContact || '',
     CONTACT_EMAIL: bandInfo.email || '',
     CONTACT_PHONE: bandInfo.phone || '',
     
     // Enhanced International variables
     LOCATIONA: '',
     LOCATIONB: '',
     LOCATIONC: '',
     LOCATIOND: '',
     INBOUNDAIRLINE: '',
     INBOUND_FLIGHT_NO: '',
     INBOUND_BOOKING_REF: '',
     OUTBOUNDAIRLINE: '',
     OUTBOUND_FLIGHT_NO: '',
     OUTBOUND_BOOKING_REF: '',
     INBOUNDTRAIN: '',
     INBOUND_TRAIN_NO: '',
     OUTBOUNDTRAIN: '',
     OUTBOUND_TRAIN_NO: '',
     INBOUND_BUS: '',
     INBOUND_BUS_NO: '',
     OUTBOUND_BUS: '',
     OUTBOUND_BUS_NO: '',
     INBOUND_CAR: '',
     INBOUND_CAR_NO: '',
     OUTBOUND_CAR: '',
     OUTBOUND_CAR_NO: '',
     HOTEL: '',
     ARRIVAL_DATE: '',
     DEPARTURE_DATE: '',
     
     // Residency variables
     ARTISTIC_DIRECTOR: '',
     TECH_MANAGER: '',
     HOTEL_NAME: '',
     HOTEL_ADDRESS: '',
     RESIDENCY_DURATION: '3',
     LOCAL_ARTIST_1: '',
     LOCAL_ARTIST_2: '',
     LOCAL_ARTIST_3: '',
     COMMUNITY_PARTNER_1: '',
     COMMUNITY_PARTNER_2: '',
     CULTURAL_SITE: '',
     RESTAURANT: ''
   };
   
   console.log('🔍 variables prepared:', variables);
   
   // Auto-detect schedule type
   const scheduleType = venueData.name?.toLowerCase().includes('navigator') ? 'residency' : 'performance';
   const isInternational = false; // Default, user can change in TourScheduler
   
   console.log('🔍 scheduleType:', scheduleType);
   console.log('🔍 isInternational:', isInternational);
   console.log('🔍 About to make API call to /api/schedules/create-from-performance');

   // Create schedule in database
   const response = await axiosInstance.post('/api/schedules/create-from-performance', {
     performanceId: performance._id,
     scheduleData: {
       variables,
       scheduleType,
       isInternational
     }
   });

   console.log('🔍 API response received:', response.data);

   const { schedule, resource } = response.data;
   
   console.log('✅ Schedule created in database:', schedule._id);
   console.log('✅ Resource created/updated:', resource._id);

   // Navigate to TourScheduler with database IDs
   navigate('/admin/performances/schedule', { 
     state: { 
       scheduleId: schedule._id,
       resourceId: resource._id,
       performanceData: {
         bandName: variables.ARTIST,
         contactName: variables.CONTACT_NAME,
         contactEmail: variables.CONTACT_EMAIL,
         contactPhone: variables.CONTACT_PHONE,
         code: performance.code || null,
         programmingId: performance.programmingId,
         venue: {
           name: variables.VENUE,
           location: variables.VENUE_ADDRESS
         },
         timing: {
           date: performance.startTime ? new Date(performance.startTime) : null,
           startTime: performance.startTime ? new Date(performance.startTime) : null,
           endTime: performance.endTime ? new Date(performance.endTime) : null
         }
       }
     }
   });

 } catch (error: any) {
   console.error('🔍 Error creating schedule:', error);
   console.error('🔍 Error response:', error.response?.data);
   
   // Show user-friendly error message
   alert(`Failed to create schedule: ${error.response?.data?.message || error.message}`);
 } finally {
   console.log('🔍 setIsLoading(false) called');
   setIsLoading(false);
 }
};

const contractButtonProps = getContractButtonProps();

const ActionButton = ({ 
  icon, 
  children, 
  color = "primary", 
  onClick, 
  disabled,
  component,
  to,
  variant = "contained"
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  onClick?: () => void;
  disabled?: boolean;
  component?: any;
  to?: string;
  variant?: "contained" | "outlined";
}) => (
  <Button
    variant={variant}
    color={color}
    disabled={disabled}
    onClick={onClick}
    component={component}
    to={to}
    fullWidth
    sx={{
      display: 'flex',
      height: '40px',
      padding: 0,
      borderRadius: '26px',
      overflow: 'hidden',
      '& .MuiButton-startIcon': {
        margin: 0,
      }
    }}
  >
    <Box sx={{
      width: '52px',
      height: '52px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRight: '1px solid rgba(255,255,255,0.2)',
      bgcolor: 'rgba(0,0,0,0.1)',
    }}>
      {icon}
    </Box>
    <Box sx={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      fontSize: '0.95rem',
      fontWeight: 500,
    }}>
      {children}
    </Box>
  </Button>
);

// Update the calculateTotalCosts function to ensure it always returns a number
const calculateTotalCosts = () => {
  if (!performance.costs) return 0;
  
  try {
    // Calculate manually based on the TypeScript interface structure
    const bandFee = parseFloat(performance.costs.bandFee?.toString() || '0');
    
    // Calculate travel costs by summing individual components
    const travelCosts = 
      parseFloat(performance.costs.travel?.flights?.toString() || '0') + 
      parseFloat(performance.costs.travel?.groundTransport?.toString() || '0') + 
      parseFloat(performance.costs.travel?.other?.toString() || '0');
    
    // Based on the errors, handle accommodation as numbers
    const accommodationCosts = 
      parseFloat(performance.costs.accommodation?.singleRooms?.toString() || '0') + 
      parseFloat(performance.costs.accommodation?.twinRooms?.toString() || '0') + 
      parseFloat(performance.costs.accommodation?.costPerRoom?.toString() || '0') + 
      parseFloat(performance.costs.accommodation?.totalNights?.toString() || '0');
    
    // Calculate per diem costs
    const perDiemCosts = 
      parseFloat(performance.costs.perDiem?.dailyRate?.toString() || '0') * 
      parseFloat(performance.costs.perDiem?.numberOfDays?.toString() || '1');
    
    // Equipment costs
    const equipmentCosts = 
      parseFloat(performance.costs.equipment?.rental?.toString() || '0') + 
      parseFloat(performance.costs.equipment?.shipping?.toString() || '0');
    
    // Calculate technical costs
    const technicalCosts = 
      parseFloat(performance.costs.technical?.soundEngineer?.toString() || '0') + 
      parseFloat(performance.costs.technical?.lightingEngineer?.toString() || '0') + 
      parseFloat(performance.costs.technical?.backline?.toString() || '0') + 
      parseFloat(performance.costs.technical?.other?.toString() || '0');
    
    // Other costs at the top level
    const otherCosts = parseFloat(performance.costs.other?.toString() || '0');
    
    // Sum of all costs
    return bandFee + travelCosts + accommodationCosts + perDiemCosts + 
           equipmentCosts + technicalCosts + otherCosts;
  } catch (error) {
    console.error('Error calculating total costs:', error);
    return 0; // Return 0 as a fallback
  }
};

// Update this to ensure we handle any type issues
const totalCosts = parseFloat(calculateTotalCosts().toString() || '0');

return (
  <Card sx={{ mb: 4 }}>
    <CardContent>
      <Typography variant="h5" gutterBottom sx={{ 
        display: 'flex', 
        alignItems: 'center' 
      }}>
        <Article sx={{ mr: 1 }} />
        Actions
      </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 1.5
      }}>
        <ActionButton
          icon={<ModeEdit sx={{ fontSize: 28 }} />}
          color="info"
          onClick={() => navigate(`/admin/performances/edit/${performance._id}`)}
        >
          Edit Performance Details
        </ActionButton>

        <ActionButton
          icon={<AttachFile sx={{ fontSize: 28 }} />}
          color="warning"
          component={Link}
    to={`/admin/resources/${performance._id}`}
        >
          Resources
        </ActionButton>

        <ActionButton
          icon={<Groups2 sx={{ fontSize: 28 }} />}
          color="success"
          onClick={onManageStaffClick}
        >
          Manage Staff
        </ActionButton>

        <ActionButton
          icon={<Email sx={{ fontSize: 28 }} />}
          color="secondary"
          onClick={() => {
            setCurrentContact(artistContact);
            setContactModalOpen(true);
          }}
          disabled={!artistContact}
        >
          Contact Artist
        </ActionButton>

        <ActionButton
          icon={<LocationOn sx={{ fontSize: 28 }} />}
          color="primary"
          onClick={() => {
            setCurrentContact(venueContact);
            setContactModalOpen(true);
          }}
          disabled={!venueContact}
        >
          Contact Primary
        </ActionButton>
        {technicalContact && (
  <ActionButton
    icon={<FactCheck sx={{ fontSize: 28 }} />}
    color="info"
    onClick={() => {
      setCurrentContact(technicalContact);
      setContactModalOpen(true);
    }}
  >
    Contact Venue Tech
  </ActionButton>
)}

        {hasBandRecord && (
          <ActionButton
            icon={<MusicNote sx={{ fontSize: 28 }} />}
            variant="outlined"
            component={Link}
            to={`/band/${(performance.band as BandRecommendation)._id}`}
          >
            View Band Profile
          </ActionButton>
        )}

        <ActionButton
          icon={<ConfirmationNumberRounded sx={{ fontSize: 28 }} />}
          color="warning"
          onClick={handleTicketReview}
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : 'Review Ticketing'}
        </ActionButton>

        <ActionButton
  icon={<LocalActivity sx={{ fontSize: 28 }} />}
  color="secondary"
  onClick={handleViewTicketSales}
  disabled={isLoading}
>
  {isLoading ? 'Loading...' : 'View Ticket Sales'}
</ActionButton>

<ActionButton
  icon={<Update sx={{ fontSize: 28 }} />}
  color="success"
  onClick={handleCreateSchedule}
  disabled={isLoading || checkingSchedule}
>
  {isLoading ? 'Loading...' : 
   checkingSchedule ? 'Checking...' :
   existingSchedule ? 'Edit Schedule' : 'Create Schedule'}
</ActionButton>


<ActionButton
  icon={<ReceiptLong sx={{ fontSize: 28 }} />}
  color={contractButtonProps.color}
  onClick={contractButtonProps.action}
  disabled={contractButtonProps.disabled}
>
  {contractButtonProps.text}
</ActionButton>
        <ActionButton
  icon={<RequestQuote sx={{ fontSize: 28 }} />}
  color="success"
  onClick={handleRequestInvoice}
  disabled={isLoading || (!artistContact?.email && typeof performance.band !== 'object')}
>
  {isLoading ? 'Please wait...' : 'Request Invoice'}
</ActionButton>

<ActionButton
  icon={<ReceiptLong sx={{ fontSize: 28 }} />}
  color="info"
  onClick={handlePOButtonClick}
  disabled={isLoading || loadingPOs}
>
  {isLoading || loadingPOs ? 'Loading...' : 
   performance.purchaseOrderNumber ? 
   `Purchase Order (${performance.purchaseOrderNumber})` : 
   'Create Purchase Order'}
</ActionButton>

<ActionButton
  icon={<ArrowForward sx={{ fontSize: 28 }} />}
  color="primary"
  component={Link}
  to={`/admin/performances/post-production/${performance._id}`}
>
  Post-Production
</ActionButton>

      </Box>
        
        {currentContact && (
<ContactModal
  open={contactModalOpen}
  onClose={() => setContactModalOpen(false)}
  contactData={currentContact}
/>
        )}

<Dialog
  open={titoSalesDialogOpen}
  onClose={() => setTitoSalesDialogOpen(false)}
  maxWidth="md"
  fullWidth
>
  <DialogTitle>
    Ticket Sales - {
      performance.simpleBandName || 
      (typeof performance.band === 'object' ? performance.band?.artistName : 'Event')
    }
    {ticketsNotOnSale && <Typography variant="subtitle1" color="text.secondary">(Not On Sale Yet)</Typography>}
  </DialogTitle>
  <DialogContent>
    {titoSalesLoading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    ) : titoSalesError ? (
      <Alert severity="info" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          This event is not available on Tito yet
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          It appears this event hasn't been set up in the ticketing system yet. This could be because:
        </Typography>
        <ul>
          <li>The event is not on sale yet</li>
          <li>The event is still in planning phase</li>
          <li>Tickets will be handled through a different system</li>
        </ul>
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px dashed' }}>
          <Typography variant="subtitle2" gutterBottom>
            For the event manager:
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            When creating this event in Tito, use slug: <strong>{titoSlug}</strong>
          </Typography>
        </Box>
      </Alert>
    ) : !titoSalesData ? (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Alert severity="info">
          Looking for ticket data with slug: <strong>{titoSlug}</strong>
        </Alert>
      </Box>
  ) : (
    <Box sx={{ p: 2 }}>
      {/* Event Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Event Details</Typography>
            <Typography><strong>Event:</strong> {titoSalesData.event.title}</Typography>
            <Typography><strong>Tito Slug:</strong> {titoSlug}</Typography>
            <Typography><strong>Start Date:</strong> {new Date(titoSalesData.event.start_date).toLocaleDateString()}</Typography>
            {titoSalesData.event.end_date && (
              <Typography><strong>End Date:</strong> {new Date(titoSalesData.event.end_date).toLocaleDateString()}</Typography>
            )}
          </Grid>

    <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.4rem' }}>
              Sales Summary
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <People sx={{ mr: 1, fontSize: 28 }} />
              <Typography sx={{ fontSize: '1.2rem' }}>
                <strong>Tickets Sold:</strong>{' '}
                <span
                  style={{
                    fontSize: '1.6rem',
                    fontWeight: 'bold',
                    color: '#FFD700',
                    padding: '2px 8px',
                    borderRadius: '4px'
                  }}
                >
                  {toNum(titoSalesData.totalSold)}
                </span>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Euro sx={{ mr: 1, fontSize: 28 }} />
              <Typography sx={{ fontSize: '1.2rem' }}>
                <strong>Total Revenue:</strong>{' '}
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                  €{toNum(titoSalesData.totalRevenue).toFixed(2)}
                </span>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Ticket Types Table */}
      <Typography variant="h6" gutterBottom>
        Ticket Types
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ticket Type</TableCell>
              <TableCell align="right">Price (€)</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell align="right">Sold</TableCell>
              <TableCell align="right">Available</TableCell>
              <TableCell align="right">Revenue (€)</TableCell>
            </TableRow>
          </TableHead>
         <TableBody>
  {titoSalesData.ticketTypes.map((type: any, idx: number) => {
    const price   = parseFloat(type.price as string) || 0;
    const qty     = parseInt(type.quantity as string, 10) || 0;
    const sold    = parseInt(type.sold as string, 10) || 0;
    const avail   = qty - sold;
    const revenue = parseFloat(type.revenue as string) || 0;

    return (
      <TableRow key={idx}>
        <TableCell>{type.name}</TableCell>
        <TableCell align="right">{price.toFixed(2)}</TableCell>
        <TableCell align="right">{qty}</TableCell>
        <TableCell align="right">{sold}</TableCell>
        <TableCell align="right">{avail}</TableCell>
        <TableCell align="right">€{revenue.toFixed(2)}</TableCell>
      </TableRow>
    );
  })}

  <TableRow sx={{ fontWeight: 'bold' }}>
    <TableCell colSpan={2}>Total</TableCell>
    <TableCell align="right">
      {titoSalesData.ticketTypes.reduce(
        (sum: number, t: any) => sum + (parseInt(t.quantity as string, 10) || 0),
        0
      )}
    </TableCell>
    <TableCell align="right">{titoSalesData.totalSold}</TableCell>
    <TableCell align="right">
      {titoSalesData.ticketTypes.reduce(
        (sum: number, t: any) =>
          sum + ((parseInt(t.quantity as string, 10) || 0) - (parseInt(t.sold as string, 10) || 0)),
        0
      )}
    </TableCell>
    <TableCell align="right">
      €{titoSalesData.totalRevenue.toFixed(2)}
    </TableCell>
  </TableRow>
</TableBody>
        </Table>
      </TableContainer>

      {/* Compare to Budget */}
      {performance.costs && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Revenue vs Costs
          </Typography>
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Total Revenue from Tito</TableCell>
                  <TableCell align="right">
                    €{toNum(titoSalesData.totalRevenue).toFixed(2)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Budgeted Costs</TableCell>
                  <TableCell align="right">
                    €{toNum(totalCosts).toFixed(2)}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      fontWeight: 'bold',
                      color:
                        toNum(titoSalesData.totalRevenue) >= toNum(totalCosts)
                          ? 'success.main'
                          : 'error.main'
                    }
                  }}
                >
                  <TableCell>Profit/Loss</TableCell>
                  <TableCell align="right">
                    €
                    {(
                      toNum(titoSalesData.totalRevenue) -
                      toNum(totalCosts)
                    ).toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  )}


</DialogContent>

  <DialogActions>
    <Button onClick={() => setTitoSalesDialogOpen(false)}>Close</Button>
    {ticketsNotOnSale && (
      <Button 
        variant="contained" 
        color="primary"
        onClick={handleCopyTitoSlug}
      >
        Copy Slug
      </Button>
    )}
  </DialogActions>
</Dialog>
{performance && (
  <CreatePOModal
    open={poModalOpen}
    onClose={() => setPoModalOpen(false)}
    performance={performance}
    onSuccess={handlePOSuccess}
  />
)}
        
      </CardContent>
      
      {artistContact && (
  <InvoiceRequestModal
    open={invoiceModalOpen}
    onClose={() => setInvoiceModalOpen(false)}
    onSend={sendInvoiceRequest}
    performanceData={{
      bandName: performance.simpleBandName || 
        (typeof performance.band === 'object' ? performance.band.artistName : 'Artist'),
      contactEmail: artistContact.email || '',
      contactName: artistContact.name || '',
      code: performance.code || null,
      programmingId: performance.programmingId,
      eventDate: new Date(performance.startTime).toLocaleDateString(),
      venue: typeof performance.venue === 'object' ? performance.venue.name : 'Venue',
      activityName: performance.strandId && typeof performance.strandId === 'object' 
        ? performance.strandId.name 
        : undefined,
      fee: performance.costs?.bandFee || 0
    }}
  />
)}
    </Card>

  );
};

export default ActionSection;